import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALL_USERS, ON_USER_CHANGED } from '../../graphql/users';
import UsersList from './UsersList';
import { showInfoNotification } from '../../actions/notifications';
import Paginator from '../Paginator';
import {
    parseBooleanProperty,
    parseIncludeDeactivated, parseIncludeDeleted,
    parsePaging,
    parseQuery,
    parseSorting,
} from '../../utils/queryString';
import CreateUserRow from './CreateUserRow';
import Spinner from '../Spinner';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import { getValueMapping } from './UserRow';
import Constants from '../../utils/Constants';
import Search from '../Search';
import CheckboxField from '../fields/CheckboxField';
import SubscriptionActions from '../../utils/SubscriptionActions';
import Error from '../errors/Error';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';
import useAuthentication from '../../hooks/useAuthentication';

export function createNotificationText(t, action, name, racf) {
    let notification;
    switch (action) {
        case SubscriptionActions.USER_CREATED:
            notification = t('users.notification.user-create-thirdparty', { name, racf });
            break;
        case SubscriptionActions.USER_UPDATED:
            notification = t('users.notification.user-update-thirdparty', { name, racf });
            break;
        default:
            notification = t('users.notification.users-modified-thirdparty');
    }
    return notification;
}

function Users() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'name',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const query = parseQuery(location.search);
    const includeDeactivated = parseIncludeDeactivated(location.search);
    const includeDeleted = parseIncludeDeleted(location.search);
    const usersWithoutMembersOnly = parseBooleanProperty(location.search, 'usersWithoutMembersOnly');
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        query,
        includeDeactivated,
        includeDeleted,
        usersWithoutMembersOnly,
        sortDirection,
        sortKey,
    });
    const {
        loading,
        error,
        data,
        refetch,
        previousData
    } = useExtendedQuery(ALL_USERS, {
        variables: refetchProps,
        valueMappingFn: getValueMapping,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    const onSearch = (newQuery) => setRefetchProps({
        ...refetchProps,
        query: newQuery,
        page: 0,
    });
    const onIncludeDeactivated = (newIncludeDeactivated) => setRefetchProps({
        ...refetchProps,
        includeDeactivated: newIncludeDeactivated,
        includeDeleted: refetchProps.includeDeleted && newIncludeDeactivated,
        page: 0,
    });
    const onIncludeDeleted = (newIncludeDeleted) => setRefetchProps({
        ...refetchProps,
        includeDeleted: newIncludeDeleted,
        includeDeactivated: newIncludeDeleted || refetchProps.includeDeactivated,
        page: 0,
    });
    const onUsersWithoutMembersOnly = (newUsersWithoutMembersOnly) => setRefetchProps({
        ...refetchProps,
        usersWithoutMembersOnly: newUsersWithoutMembersOnly,
        page: 0,
    });
    useExtendedSubscription(ON_USER_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'userChanged',
        onChangeCallback: ({ action, model: { name, racf } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.USER_EDIT])) {
                const notification = createNotificationText(t, action, name, racf);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let searchResult;
    let userProps = null;
    let searchMatches = 0;
    if (loading) {
        result = <Spinner className="users-loading" />;
    } else if (error) {
        result = <Error className="users-error" error={error} />;
    } else {
        searchResult = data === undefined ? previousData : data;
        searchMatches = searchResult.allUsers.userList.totalCount;
        userProps = {
            availableRoles: searchResult.allUsers.availableRoles,
            availableAssignedZones: searchResult.allUsers.availableAssignedZones,
            assignableMembers: searchResult.allUsers.assignableMembers,
            allowedRoleCombinations: searchResult.allUsers.allowedRoleCombinations,
            clearingEnabled: searchResult.allUsers.clearingEnabled,
        };
        result = (
            <Paginator
                totalCount={searchResult.allUsers.userList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <UsersList
                    users={searchResult.allUsers.userList.models}
                    userProps={userProps}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4><FontAwesomeIcon icon="users" />&nbsp;{t('title.users')}</h4>
            {userProps && <CreateUserRow userProps={userProps} />}
            <div className="search-and-filter">
                <Search
                    placeholder={t('users.placeholder.search')}
                    query={refetchProps.query}
                    onSearch={onSearch}
                    matches={searchMatches}
                />
                <div className="filter">
                    <CheckboxField
                        label={t('users.include-deleted')}
                        value={refetchProps.includeDeleted}
                        onChange={onIncludeDeleted}
                        className="users--search-and-filter--include-deleted"
                        inputId="users--search-and-filter--include-deleted"
                    />
                    <CheckboxField
                        label={t('users.include-deactivated')}
                        value={refetchProps.includeDeactivated}
                        onChange={onIncludeDeactivated}
                        className="users--search-and-filter--include-deactivated"
                        inputId="users--search-and-filter--include-deactivated"
                    />
                    <CheckboxField
                        label={t('users.users-without-members-only')}
                        value={refetchProps.usersWithoutMembersOnly}
                        onChange={onUsersWithoutMembersOnly}
                        className="users--search-and-filter--users-without-members-only"
                        inputId="users--search-and-filter--users-without-members-only"
                    />
                </div>
            </div>
            {result}
        </>
    );
}

export default Users;
